/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { ApiErrorDecoder } from './api-error-decoder';
import { NavigatorService } from '@services/navigator/navigator.service';
import { AppService } from '@services/app/app.service';
import { UiService } from '@services/ui/ui.service';
import { LabelsService } from '@services/labels/labels.service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiErrorDTO, MessageDTO } from '@api/models';
import { ERRORS } from '@core/models';
import { ToastController } from '@ionic/angular';
import { LoggerService } from '@core/services/logger/logger.service';
import { AuthRequestService } from '@core/services/auth/auth-request.service';

export interface ReqErrorConfigModel {
  showError: boolean;
  defaultMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptorService implements HttpInterceptor {
  constructor(
    private appService: AppService,
    private uiService: UiService,
    private navigator: NavigatorService,
    private labelsService: LabelsService,
    private toastController: ToastController,
    private loggerService: LoggerService,
    private authRequestService: AuthRequestService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const errorConfig = this.createErrorConfig();

    this.logRequest(req);

    this.uiService.restoreNextError();

    return next.handle(req).pipe(
      map((event: HttpResponse<any>) => this.handleSuccess(event)),
      catchError((event: HttpErrorResponse) =>
        this.handleError(event, errorConfig)
      )
    );
  }

  /**
   * crea la configurazione per la gestione dell'errore della richiesta corrente
   */
  private createErrorConfig(): ReqErrorConfigModel {
    return {
      showError: this.isNextErrorToShow(),
      defaultMessage: this.getDefaultErrorMessage()
    };
  }

  /**
   * stabilisce se è necessario mostrare l'errore all'utente
   */
  private isNextErrorToShow(): boolean {
    return this.uiService.isNextErrorToShow();
  }

  /**
   * stabilisce il messaggio di default da visualizzare nell'errore
   * qualora non sia presente nella response
   */
  private getDefaultErrorMessage(): string {
    return this.uiService.getNextErrorMessage() || 'err.something_went_wrong';
  }

  /**
   * gestisce il success
   */
  private handleSuccess(event: HttpResponse<any>): HttpResponse<any> {
    if (
      event.body &&
      event.body.metadata &&
      event.body.metadata.messages instanceof Array &&
      event.body.metadata.messages.length > 0
    ) {
      this.showMessageToast(event.body.metadata.messages[0] as MessageDTO);
    }

    return event;
  }

  /**
   * gestisce l'errore
   */
  private handleError(
    event: HttpErrorResponse,
    config: ReqErrorConfigModel
  ): Observable<never> {
    const error = event.error as ApiErrorDTO;
    const decoder = new ApiErrorDecoder(error, config.defaultMessage);

    this.logError(event);

    this.redirectOnError(event.status, decoder);

    if (config && config.showError && !this.isLogUrl(event.url)) {
      this.showErrorToast(decoder);
    }

    return throwError(event);
  }

  /**
   * applica redirect sull'errore in base alla configurazione
   */
  private redirectOnError(status: number, decoder: ApiErrorDecoder): void {
    const config = this.appService.getConfig().generic;
    const redirectUnauthorized = config.redirectUnauthorized;
    const errorsWithRedirect = config.errorsWithRedirect;
    const redirectPath =
      errorsWithRedirect && errorsWithRedirect[decoder.getCode()];

    if (status === 401 && redirectUnauthorized === true) {
      this.forceAppLoaderClosing();
      this.navigator.navigateToError(ERRORS.sessionExpired);
    } else if (redirectPath) {
      this.forceAppLoaderClosing();
      this.navigator.navigateToMainRoute(redirectPath);
    }
  }

  /**
   * in caso di redirect è necessario chiudere l'eventuale app loader aperto
   * che altrimenti non chiuderebbero
   */
  private forceAppLoaderClosing(): Promise<void> {
    return this.uiService.hideAppLoader(true);
  }

  /**
   * mostra messaggio di errore
   */
  private async showErrorToast(decoder: ApiErrorDecoder): Promise<void> {
    if (this.appService.getConfig().generic.errorDisplay === 'complete') {
      this.showCompleteErrorToast(decoder);
    } else {
      this.showBaseErrorToast(decoder);
    }
  }

  /**
   * visualizza toast message di base
   */
  private async showBaseErrorToast(decoder: ApiErrorDecoder): Promise<void> {
    const message = decoder.getMessage();

    this.uiService.showErrorToast(message);
  }

  /**
   * visualizza toast message avanzato con codice errore
   */
  private async showCompleteErrorToast(
    decoder: ApiErrorDecoder
  ): Promise<void> {
    const uuid = decoder.getPartialUuid();
    const message = decoder.getMessage();

    const toast = await this.toastController.create({
      header: this.labelsService.getLabel(message),
      message: `Error code: ${uuid}`,
      position: 'top',
      color: 'danger',
      duration: 8000,
      cssClass: 'complete-error-toast',
      buttons: [
        {
          icon: 'close',
          role: 'cancel',
          handler: () => toast.dismiss()
        }
      ]
    });

    await toast.present();
  }

  /**
   * visualizza messaggio di warning
   */
  private async showMessageToast(message: MessageDTO): Promise<void> {
    if (!message || !message.code) {
      return;
    }

    const label = this.labelsService.getLabel(`wrn.message_${message.code}`);

    switch (message.type) {
      case 'ERROR':
        return await this.uiService.showErrorToast(label);
      case 'SUCCESS':
        return await this.uiService.showSuccessToast(label);
      case 'INFO':
        return await this.uiService.showInfoToast(label);
      case 'WARNING':
      default:
        return await this.uiService.showWarningToast(label);
    }
  }

  /**
   * logga la richiesta solo se non è l'api di log
   */
  private logRequest(req: HttpRequest<any>): void {
    if (this.isUrlToLog(req.url)) {
      this.loggerService.logHttpReq(req);
    }
  }

  /**
   * logga l'errore solo se non è l'api di log
   */
  private logError(event: HttpErrorResponse): void {
    if (this.isUrlToLog(event.url)) {
      this.loggerService.logError(event);
    }
  }

  /**
   * stabilisce se l'url è da loggaree
   */
  private isUrlToLog(url: string): boolean {
    return !this.isLogUrl(url) && !this.authRequestService.isUrlOutOfAuth(url);
  }

  /**
   * stabilisce se si tratta dell'api di log
   */
  private isLogUrl(url: string): boolean {
    return url && url.includes('user/action');
  }
}
